<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div v-if="admin_datos && admin_datos.superadmin" class="main-container user-admin-page">
        <div v-if="loading" class="user-admin-loader">
          <div class="spinner-border text-secondary" role="status"></div>
          <span>Cargando usuario...</span>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <template v-else-if="usuario">
          <header class="user-admin-header">
            <div class="header-main">
              <button type="button" class="back-button" @click="$router.back()">
                <b-icon icon="arrow-left" />
              </button>
              <div>
                <div class="eyebrow">Detalle de cliente</div>
                <h1>{{ userName }}</h1>
                <div class="header-meta">
                  <span :class="['status-badge', usuario.suspendido ? 'danger' : 'success']">
                    {{ usuario.suspendido ? 'Suspendido' : 'Activo' }}
                  </span>
                  <span :class="['status-badge', usuario.verificado ? 'primary' : 'muted']">
                    {{ usuario.verificado ? 'Verificado' : 'No verificado' }}
                  </span>
                  <span>{{ userTypeLabel }}</span>
                  <span>Cliente #{{ usuario.id }}</span>
                </div>
              </div>
            </div>
            <div class="header-actions">
              <router-link class="action-button secondary" to="/superadmin/Usuarios">
                <b-icon icon="list-ul" />
                Lista de usuarios
              </router-link>
              <button
                type="button"
                class="action-button secondary"
                :disabled="tipoLoading || userType === 'inversionista'"
                @click="updateTipo('inversionista')"
              >
                <b-icon icon="person-badge" />
                Inversionista
              </button>
              <button
                type="button"
                class="action-button"
                :class="usuario.suspendido ? 'success-button' : 'danger-button'"
                :disabled="actionLoading"
                @click="toggleSuspension"
              >
                <b-icon :icon="usuario.suspendido ? 'unlock' : 'lock'" />
                {{ usuario.suspendido ? 'Reactivar' : 'Suspender' }}
              </button>
            </div>
          </header>

          <section class="summary-grid">
            <div class="summary-card">
              <div class="summary-icon orders"><b-icon icon="cart2" /></div>
              <div class="summary-copy">
                <span class="summary-label">Ordenes</span>
                <strong>{{ stats.ordenes_total || 0 }}</strong>
                <span>{{ stats.ordenes_activas || 0 }} activas</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon money"><b-icon icon="cash-stack" /></div>
              <div class="summary-copy">
                <span class="summary-label">Pagado</span>
                <strong>{{ formatCurrency(stats.monto_pagado_total) }}</strong>
                <span>Total ordenes: {{ formatCurrency(stats.monto_total) }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon requests"><b-icon icon="inbox" /></div>
              <div class="summary-copy">
                <span class="summary-label">Solicitudes</span>
                <strong>{{ stats.solicitudes_total || 0 }}</strong>
                <span>Ultimo mov.: {{ formatDate(stats.ultimo_movimiento_iso) }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon properties"><b-icon icon="house-door" /></div>
              <div class="summary-copy">
                <span class="summary-label">Propiedades</span>
                <strong>{{ stats.propiedades_total || 0 }}</strong>
                <span>{{ propiedades.length }} configuradas</span>
              </div>
            </div>
          </section>

          <div class="content-grid">
            <main class="content-main">
              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon timeline-icon"><b-icon icon="clock-history" /></span>Linea de tiempo</h2>
                </div>
                <div v-if="timeline.length" class="timeline">
                  <div v-for="event in timeline" :key="event.key" class="timeline-item">
                    <div class="timeline-dot" :class="event.kind"></div>
                    <div>
                      <router-link v-if="event.to" class="timeline-title timeline-link" :to="event.to">{{ event.title }}</router-link>
                      <div v-else class="timeline-title">{{ event.title }}</div>
                      <div class="timeline-date">{{ formatDateTime(event.date) }}</div>
                      <p v-if="event.description">{{ event.description }}</p>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">Sin actividad registrada.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon orders-icon"><b-icon icon="cart2" /></span>Ordenes</h2>
                  <span>{{ ordenes.length }}</span>
                </div>
                <div v-if="ordenes.length" class="table-wrap">
                  <table class="admin-table">
                    <thead>
                      <tr>
                        <th>Orden</th>
                        <th>Fecha</th>
                        <th>Profesional</th>
                        <th>Estado</th>
                        <th class="text-right">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="orden in ordenes" :key="orden.id">
                        <td>
                          <router-link :to="`/OrdenDetalleAdmin/${orden.id}`">#{{ orden.id }}</router-link>
                          <small>{{ orderAddress(orden) }}</small>
                        </td>
                        <td>{{ formatDate(orden.fecha_iso || orden.fecha_creado_iso) }}</td>
                        <td>{{ orden.profesional_nombre || 'Sin profesional' }}</td>
                        <td><span :class="['mini-badge', orderStatusClass(orden.estado)]">{{ orden.estado_label }}</span></td>
                        <td class="text-right">{{ formatCurrency(orden.monto_total || orden.monto) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="empty-state">Sin ordenes asociadas.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon requests-icon"><b-icon icon="inbox" /></span>Solicitudes</h2>
                  <span>{{ solicitudes.length }}</span>
                </div>
                <div v-if="solicitudes.length" class="stack-list">
                  <article v-for="solicitud in solicitudes" :key="solicitud.id" class="list-row request-row">
                    <div>
                      <router-link class="request-link" :to="`/SolicitudDetalleAdmin/${solicitud.id}`">
                        Solicitud #{{ solicitud.id }} {{ solicitud.titulo ? `- ${solicitud.titulo}` : '' }}
                      </router-link>
                      <span>{{ formatDateTime(solicitud.fecha_creado_iso) }}</span>
                      <span>{{ solicitud.direccion || 'Sin direccion' }}</span>
                    </div>
                    <div class="row-right">
                      <span class="mini-badge info">{{ solicitud.ordenes_count }} orden{{ solicitud.ordenes_count === 1 ? '' : 'es' }}</span>
                      <span class="mini-badge muted">{{ solicitud.mensajes_count }} mensajes</span>
                    </div>
                  </article>
                </div>
                <div v-else class="empty-state">Sin solicitudes.</div>
              </section>
            </main>

            <aside class="content-side">
              <section class="panel contact-panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon customer-icon"><b-icon icon="person-lines-fill" /></span>Contacto</h2>
                </div>
                <div class="contact-name">{{ userName }}</div>
                <a v-if="usuario.email" :href="`mailto:${usuario.email}`">{{ usuario.email }}</a>
                <a v-if="usuario.telefono" :href="`tel:${usuario.telefono}`">{{ usuario.telefono }}</a>
                <div class="detail-line">
                  <span>Tipo</span>
                  <strong>{{ userTypeLabel }}</strong>
                </div>
                <div class="detail-line">
                  <span>Ultimo movimiento</span>
                  <strong>{{ formatDateTime(stats.ultimo_movimiento_iso) }}</strong>
                </div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon payments-icon"><b-icon icon="credit-card" /></span>Pagos</h2>
                  <span>{{ pagos.length }}</span>
                </div>
                <div v-if="pagos.length" class="stack-list compact">
                  <div v-for="pago in pagos.slice(0, 12)" :key="`${pago.orden_id}-${pago.id}`" class="list-row">
                    <div>
                      <strong>{{ formatCurrency(pago.monto) }}</strong>
                      <span>Orden #{{ pago.orden_id }}</span>
                      <span>{{ formatDateTime(pago.fecha_pagado_iso || pago.fecha_creado_iso) }}</span>
                    </div>
                    <span :class="['mini-badge', paymentStatusClass(pago.estado)]">{{ paymentStatusLabel(pago.estado) }}</span>
                  </div>
                </div>
                <div v-else class="empty-state">Sin pagos.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon properties-icon"><b-icon icon="house-door" /></span>Propiedades</h2>
                  <span>{{ propiedades.length }}</span>
                </div>
                <div v-if="propiedades.length" class="stack-list compact">
                  <div v-for="propiedad in propiedades" :key="propiedad.id" class="list-row property-row">
                    <div>
                      <strong>{{ propiedad.nombre || `Propiedad #${propiedad.id}` }}</strong>
                      <span>{{ propertyAddress(propiedad) }}</span>
                      <span v-if="propiedad.empresa_administracion_nombre">{{ propiedad.empresa_administracion_nombre }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">Sin propiedades configuradas.</div>
              </section>
            </aside>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import SideNav from '@/components/SideNav.vue'
import TopNav from '@/components/TopNav.vue'

export default {
  name: 'UsuarioDetalle',
  components: {
    SideNav,
    TopNav
  },
  data () {
    return {
      loading: true,
      error: '',
      payload: null,
      actionLoading: false,
      tipoLoading: false
    }
  },
  computed: {
    ...mapGetters(['admin_datos']),
    usuario () {
      return this.payload && this.payload.usuario ? this.payload.usuario : null
    },
    stats () {
      return this.payload && this.payload.stats ? this.payload.stats : {}
    },
    ordenes () {
      return this.payload && Array.isArray(this.payload.ordenes) ? this.payload.ordenes : []
    },
    solicitudes () {
      return this.payload && Array.isArray(this.payload.solicitudes) ? this.payload.solicitudes : []
    },
    propiedades () {
      return this.payload && Array.isArray(this.payload.propiedades) ? this.payload.propiedades : []
    },
    pagos () {
      return this.payload && Array.isArray(this.payload.pagos) ? this.payload.pagos : []
    },
    userName () {
      if (!this.usuario) return 'Cliente'
      const full = `${this.usuario.nombre || ''} ${this.usuario.apellidos || ''}`.trim()
      return full || this.usuario.email || `Cliente #${this.usuario.id}`
    },
    userType () {
      return this.usuario ? (this.usuario.tipo || 'normal').toString().toLowerCase() : 'normal'
    },
    userTypeLabel () {
      const labels = {
        inversionista: 'Inversionista',
        normal: 'Usuario'
      }
      return labels[this.userType] || this.userType
    },
    timeline () {
      const events = []
      this.ordenes.slice(0, 12).forEach(orden => {
        this.addTimeline(events, `orden-${orden.id}`, `Orden #${orden.id} ${orden.estado_label || ''}`.trim(), orden.fecha_creado_iso, this.orderAddress(orden), 'primary')
        if (orden.fecha_pagado_iso) {
          this.addTimeline(events, `orden-pago-${orden.id}`, `Pago en orden #${orden.id}`, orden.fecha_pagado_iso, this.formatCurrency(orden.monto_pagado), 'success')
        }
      })
      this.solicitudes.slice(0, 12).forEach(solicitud => {
        this.addTimeline(events, `solicitud-${solicitud.id}`, `Solicitud #${solicitud.id}`, solicitud.fecha_creado_iso, solicitud.titulo || solicitud.descripcion, 'info', `/SolicitudDetalleAdmin/${solicitud.id}`)
      })
      this.pagos.slice(0, 12).forEach(pago => {
        this.addTimeline(events, `pago-${pago.id}`, `Pago #${pago.id}`, pago.fecha_pagado_iso || pago.fecha_creado_iso, this.formatCurrency(pago.monto), 'success')
      })
      return events.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).slice(0, 20)
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler () {
        this.fetchUsuario()
      }
    },
    admin_datos: {
      handler (val) {
        if (val && !val.superadmin) {
          this.$router.push('/')
        }
      },
      deep: true
    }
  },
  methods: {
    fetchUsuario () {
      if (this.admin_datos && !this.admin_datos.superadmin) {
        this.$router.push('/')
        return
      }
      const id = this.$route.params.id
      this.loading = true
      this.error = ''
      axios.get(`${window.hostname}api/admin/usuarios/${id}/detalle`)
        .then(response => {
          this.payload = response.data
        })
        .catch(error => {
          console.error('Error loading user detail', error)
          this.error = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'No se pudo cargar el usuario.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    toggleSuspension () {
      if (!this.usuario) return
      const action = this.usuario.suspendido ? 'unsuspend_usuario' : 'suspend_usuario'
      this.actionLoading = true
      axios.post(window.hostname + `api/admin/${action}`, { usuario_id: this.usuario.id })
        .then(resp => {
          const updated = resp.data && resp.data.usuario ? resp.data.usuario : null
          if (updated && this.payload) {
            this.$set(this.payload, 'usuario', updated)
          }
          this.$notify({
            group: 'foos',
            title: 'Exito',
            text: resp.data && resp.data.message ? resp.data.message : 'Estado actualizado.',
            type: 'success'
          })
        })
        .catch(err => {
          console.error(err)
          const message = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'No se pudo actualizar el estado.'
          this.$notify({ group: 'foos', title: 'Error', text: message, type: 'error' })
        })
        .finally(() => {
          this.actionLoading = false
        })
    },
    updateTipo (tipo) {
      if (!this.usuario) return
      this.tipoLoading = true
      axios.post(window.hostname + 'api/admin/actualizar_usuario_tipo', {
        usuario_id: this.usuario.id,
        tipo
      })
        .then(resp => {
          const updated = resp.data && resp.data.usuario ? resp.data.usuario : null
          if (updated && this.payload) {
            this.$set(this.payload, 'usuario', updated)
          }
          this.$notify({
            group: 'foos',
            title: 'Exito',
            text: resp.data && resp.data.message ? resp.data.message : 'Tipo actualizado.',
            type: 'success'
          })
        })
        .catch(err => {
          console.error(err)
          const message = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'No se pudo actualizar el tipo.'
          this.$notify({ group: 'foos', title: 'Error', text: message, type: 'error' })
        })
        .finally(() => {
          this.tipoLoading = false
        })
    },
    addTimeline (events, key, title, date, description, kind, to = null) {
      if (!date) return
      events.push({ key, title, date, description, kind, to })
    },
    formatCurrency (value) {
      const number = Number(value) || 0
      return number.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
    },
    formatDate (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleDateString('es-CL', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatDateTime (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleString('es-CL', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    orderAddress (orden) {
      if (!orden) return 'Sin direccion'
      const comuna = orden.comuna && orden.comuna.nombre ? orden.comuna.nombre : orden.comuna_nombre
      return [orden.direccion, orden.n_depto ? `Depto/Casa ${orden.n_depto}` : '', comuna].filter(Boolean).join(', ') || 'Sin direccion'
    },
    propertyAddress (propiedad) {
      if (!propiedad) return 'Sin direccion'
      const comuna = propiedad.comuna && propiedad.comuna.nombre ? propiedad.comuna.nombre : ''
      return [propiedad.direccion, propiedad.numero_apartamento, comuna].filter(Boolean).join(', ') || 'Sin direccion'
    },
    orderStatusClass (estado) {
      const classes = {
        0: 'muted',
        1: 'info',
        2: 'primary',
        3: 'warning',
        4: 'success'
      }
      return classes[estado] || 'muted'
    },
    paymentStatusLabel (estado) {
      const labels = {
        0: 'Pendiente',
        1: 'Pagado',
        2: 'Reversado',
        3: 'En revision'
      }
      return labels[estado] || `Estado ${estado}`
    },
    paymentStatusClass (estado) {
      const classes = {
        0: 'warning',
        1: 'success',
        2: 'danger',
        3: 'info'
      }
      return classes[estado] || 'muted'
    }
  }
}
</script>

<style scoped>
.column-right{
  background-color: rgb(242, 247, 251);
  padding-top:80px;
  padding-left:260px;
  width:100%;
  min-height:100vh;
}

.main-container{
  padding:22px;
}

.user-admin-page{
  color:#0f172a;
}

.user-admin-loader{
  min-height:360px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:12px;
  color:#64748b;
  font-weight:700;
}

.user-admin-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:18px;
  margin-bottom:18px;
}

.header-main{
  display:flex;
  align-items:flex-start;
  gap:14px;
  min-width:0;
}

.back-button{
  width:42px;
  height:42px;
  border-radius:12px;
  border:1px solid #dbe3ef;
  background:#fff;
  color:#334155;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
}

.eyebrow{
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
  color:#64748b;
}

h1{
  font-size:28px !important;
  line-height:1.15 !important;
  margin:2px 0 8px !important;
  font-weight:800 !important;
  color:#0f172a;
}

.header-meta{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  color:#64748b;
  font-size:13px;
  font-weight:600;
}

.header-actions{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;
  justify-content:flex-end;
}

.action-button{
  min-height:40px;
  border-radius:10px;
  padding:0 13px;
  display:inline-flex;
  align-items:center;
  gap:7px;
  font-weight:700;
  font-size:13px;
  text-decoration:none;
  border:1px solid #dbe3ef;
  background:#fff;
  color:#0f172a;
}

.action-button:disabled{
  opacity:0.55;
  cursor:not-allowed;
}

.action-button.secondary{
  background:#fff;
}

.success-button{
  background:#16a34a;
  border-color:#16a34a;
  color:#fff;
}

.danger-button{
  background:#dc2626;
  border-color:#dc2626;
  color:#fff;
}

.summary-grid{
  display:grid;
  grid-template-columns:repeat(4, minmax(0, 1fr));
  gap:12px;
  margin-bottom:18px;
}

.summary-card,
.panel{
  background:#fff;
  border:1px solid #e2e8f0;
  border-radius:8px;
  box-shadow:0 8px 22px rgba(15,23,42,0.05);
}

.summary-card{
  padding:14px;
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0;
}

.summary-icon{
  width:38px;
  height:38px;
  border-radius:10px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 38px;
  font-size:17px;
}

.summary-icon.orders,
.orders-icon{
  background:#e0f2fe;
  color:#0369a1;
}

.summary-icon.money,
.payments-icon{
  background:#fef3c7;
  color:#b45309;
}

.summary-icon.requests,
.requests-icon{
  background:#e0e7ff;
  color:#4338ca;
}

.summary-icon.properties,
.properties-icon{
  background:#dcfce7;
  color:#15803d;
}

.summary-copy{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:4px;
}

.summary-label,
.detail-line span{
  font-size:11px;
  color:#64748b;
  font-weight:800;
  text-transform:uppercase;
}

.summary-copy strong{
  font-size:16px;
  color:#0f172a;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.summary-copy span:last-child{
  font-size:12px;
  color:#64748b;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.content-grid{
  display:grid;
  grid-template-columns:minmax(0, 1fr) 360px;
  gap:18px;
  align-items:start;
}

.content-main,
.content-side{
  display:flex;
  flex-direction:column;
  gap:14px;
}

.panel{
  padding:16px;
}

.panel-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom:12px;
}

.panel-header h2{
  font-size:16px !important;
  line-height:1.2 !important;
  margin:0 !important;
  font-weight:800 !important;
  display:flex;
  align-items:center;
  gap:8px;
}

.panel-title-icon{
  width:28px;
  height:28px;
  border-radius:8px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  flex:0 0 28px;
  font-size:14px;
}

.timeline-icon{
  background:#f1f5f9;
  color:#475569;
}

.customer-icon{
  background:#ecfeff;
  color:#0e7490;
}

.table-wrap{
  overflow-x:auto;
}

.admin-table{
  width:100%;
  border-collapse:collapse;
  font-size:13px;
}

.admin-table th{
  color:#64748b;
  font-size:11px;
  text-transform:uppercase;
  padding:10px 8px;
  border-bottom:1px solid #e2e8f0;
}

.admin-table td{
  padding:11px 8px;
  border-bottom:1px solid #f1f5f9;
  vertical-align:top;
}

.admin-table td small{
  display:block;
  color:#64748b;
  margin-top:2px;
}

.admin-table a{
  color:#0369a1;
  font-weight:800;
  text-decoration:none;
}

.text-right{
  text-align:right;
}

.status-badge,
.mini-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border-radius:999px;
  font-size:11px;
  font-weight:800;
  line-height:1;
  white-space:nowrap;
}

.status-badge{
  padding:6px 10px;
}

.mini-badge{
  padding:5px 8px;
  margin:2px 3px 2px 0;
}

.primary{ background:#dbeafe; color:#1d4ed8; }
.info{ background:#e0f2fe; color:#0369a1; }
.success{ background:#dcfce7; color:#15803d; }
.warning{ background:#fef3c7; color:#b45309; }
.danger{ background:#fee2e2; color:#b91c1c; }
.muted{ background:#f1f5f9; color:#64748b; }

.stack-list{
  display:flex;
  flex-direction:column;
  gap:10px;
}

.stack-list.compact{
  gap:8px;
}

.list-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  border:1px solid #edf2f7;
  border-radius:8px;
  padding:11px;
}

.list-row div{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:2px;
}

.list-row strong{
  font-size:13px;
}

.list-row span{
  font-size:12px;
  color:#64748b;
}

.request-row,
.property-row{
  align-items:flex-start;
}

.row-right{
  align-items:flex-end;
  text-align:right;
  flex-shrink:0;
}

.timeline{
  display:flex;
  flex-direction:column;
  gap:12px;
}

.timeline-item{
  display:grid;
  grid-template-columns:18px minmax(0, 1fr);
  gap:10px;
}

.timeline-dot{
  width:10px;
  height:10px;
  border-radius:50%;
  margin-top:5px;
}

.timeline-title{
  font-size:13px;
  font-weight:800;
}

.timeline-link,
.request-link{
  color:#0369a1;
  text-decoration:none;
}

.request-link{
  font-size:13px;
  font-weight:800;
}

.timeline-date{
  color:#64748b;
  font-size:11px;
  font-weight:700;
}

.timeline p{
  margin:4px 0 0;
  color:#475569;
  font-size:12px;
  line-height:1.45;
}

.contact-panel{
  display:flex;
  flex-direction:column;
  gap:7px;
}

.contact-name{
  font-size:17px;
  font-weight:800;
}

.contact-panel a{
  color:#0369a1;
  font-size:13px;
  font-weight:700;
  text-decoration:none;
  word-break:break-word;
}

.detail-line{
  display:flex;
  flex-direction:column;
  gap:2px;
  margin-top:6px;
}

.detail-line strong{
  font-size:13px;
  color:#0f172a;
}

.empty-state{
  border:1px dashed #cbd5e1;
  border-radius:8px;
  padding:18px;
  text-align:center;
  color:#64748b;
  font-size:13px;
  font-weight:700;
}

@media (max-width: 1100px) {
  .summary-grid{
    grid-template-columns:repeat(2, minmax(0, 1fr));
  }
  .content-grid{
    grid-template-columns:1fr;
  }
}

@media (max-width: 800px) {
  .column-right{
    padding-left:0;
  }
  .user-admin-header{
    flex-direction:column;
  }
  .header-actions{
    justify-content:flex-start;
  }
}

@media (max-width: 640px) {
  .main-container{
    padding:14px;
  }
  .summary-grid{
    grid-template-columns:1fr;
  }
  h1{
    font-size:23px !important;
  }
  .list-row{
    align-items:flex-start;
    flex-direction:column;
  }
  .row-right{
    align-items:flex-start;
    text-align:left;
  }
}
</style>
